import { useId, useRef } from "react";
import type { ReactElement, ReactNode } from "react";
import { createPortal } from "react-dom";

import { useTranslation } from "../../i18n";
import { Button } from "../atoms/Button";
import { Icon } from "../atoms/Icon";
import { preferredFocus, useModalFocus } from "../useModalFocus";
import styles from "./Drawer.module.css";

export type DrawerSide = "left" | "right";

export interface DrawerProps {
  readonly open: boolean;
  /** The visible heading, which also names the sheet (from `t()`). */
  readonly title: string;
  readonly onClose: () => void;
  /** `left` for the navigation drawer under 1024px, `right` for details (the mockup's `.rdrawer`). */
  readonly side?: DrawerSide | undefined;
  /**
   * The sheet is its content, with no head and no padding (the mockup's mobile sidebar): `title`
   * then names the dialog without being drawn, and Escape or the scrim close it.
   */
  readonly headless?: boolean | undefined;
  /** `wide`: min(720px, 55vw), for content that needs room (the diagram and the details). */
  readonly size?: "default" | "wide" | undefined;
  readonly children: ReactNode;
}

/**
 * A modal side sheet: the mockup's sidebar drawer below 1024px and its right "Diagrama y detalles"
 * drawer on narrow screens. Same focus contract as `Dialog` (`useModalFocus`), a scrim that closes on
 * a press, and a close button named from the dictionary. Portalled to the body.
 */
export function Drawer({
  open,
  title,
  onClose,
  side = "right",
  headless = false,
  size = "default",
  children,
}: DrawerProps): ReactElement | null {
  const { t } = useTranslation();
  const drawerRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useModalFocus(open, drawerRef, onClose, preferredFocus);

  if (!open) return null;

  return createPortal(
    <>
      <div
        className={styles.scrim}
        role="presentation"
        onMouseDown={() => {
          onClose();
        }}
      />
      {headless ? (
        <div
          ref={drawerRef}
          className={[styles.drawer, styles[side], styles.headless].join(" ")}
          role="dialog"
          aria-modal="true"
          aria-label={title}
        >
          {children}
        </div>
      ) : (
      <div
        ref={drawerRef}
        className={[styles.drawer, styles[side], size === "wide" ? styles.wide : ""].filter(Boolean).join(" ")}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className={styles.head}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          <span className={styles.close} data-close="">
            <Button variant="ghost" iconOnly label={t("common.action.close")} onClick={onClose}>
              <Icon name="close" />
            </Button>
          </span>
        </div>
        <div className={styles.body}>{children}</div>
      </div>
      )}
    </>,
    document.body,
  );
}
